<template>
  <v-flex xs12>
    <h3 class="pb-2 mr-2 d-inline-block">
      <span class="mr-1" style="font-weight: 700;">Step {{ stepStart }}</span>-
      {{ importBehaviourText }}
    </h3>
    <v-flex xs12 px-4 py-4 mb-4 class="white-bg elevation-2 rounded-3">
      <h4>
        If there is
        <span class="mr-1" style="font-weight: 700;">no match</span>
        on the unique key?
      </h4>
      <v-radio-group class="mt-1" v-model="noMatchBehaviour" mandatory>
        <v-radio
          color="secondary"
          label="Create new rows when no match is found"
          value="create"
        />
        <v-radio
          color="secondary"
          label="Do nothing if no match is found"
          value="ignore"
        />
      </v-radio-group>
      <h4>
        If a
        <span class="mr-1" style="font-weight: 700;">match is found</span>
        on the unique key?
      </h4>
      <v-radio-group class="mt-1" v-model="matchFoundBehaviour" mandatory>
        <v-radio
          color="secondary"
          label="Update existing fields if a match is found"
          value="update"
        />
        <v-radio
          color="secondary"
          label="Do nothing if a match is found"
          value="ignore"
        />
      </v-radio-group>
    </v-flex>

    <v-flex xs12 v-if="noMatchBehaviour && matchFoundBehaviour">
      <v-flex
        xs12
        v-if="noMatchBehaviour === 'ignore' && matchFoundBehaviour === 'ignore'"
      >
        <h3 class="pb-2 mr-2 d-inline-block">
          <span class="mr-1" style="font-weight: 700;">Step {{ stepStart + 1 }}</span>
          - Check your selections above
        </h3>
        <p>Your current selections will mean your import does nothing.</p>
      </v-flex>
      <v-flex xs12 v-else>
        <h3 class="pb-2 mr-2 d-inline-block">
          <span class="mr-1" style="font-weight: 700;">Step {{ stepStart + 1 }}</span>
          - Start import
        </h3>
        <v-flex xs12 px-4 py-4 mb-4 class="white-bg elevation-2 rounded-3">
          <p class="mb-3">
            {{ mappedColumnCount }} column(s) will be imported
            <span v-if="selections.selectedFile">
              from {{ selections.selectedFile.name }}
            </span>
            .
          </p>
          <v-btn
            color="secondary"
            :loading="importing"
            :disabled="!canStart"
            @click="importData()"
          >
            Start Import
          </v-btn>
          <p v-if="importError" class="red--text mt-3 mb-0">{{ importError }}</p>
        </v-flex>
      </v-flex>
    </v-flex>
  </v-flex>
</template>

<script>
import Papa from "papaparse"
import { mapState } from "vuex"
import { rewriteCsvRows, mappedFieldNames } from "@/helpers/csvFieldMatch"

export default {
  name: "upload-import",
  props: ["dataType", "selections"],
  data: function () {
    return {
      noMatchBehaviour: "",
      matchFoundBehaviour: "",
      importing: false,
      importError: null,
    }
  },
  computed: {
    ...mapState({
      caseType: function (state) {
        return state.caseTypes.current
      },
      accountId: function (state) {
        return state.currentUser.account.id
      },
    }),
    stepStart: function () {
      return this.dataType === "atomicSelected" ? 5 : 7
    },
    importBehaviourText: function () {
      return this.dataType === "atomicSelected"
        ? "How to manage primary key scenarios"
        : "How to manage tabular import behaviour"
    },
    mappedColumnCount: function () {
      return mappedFieldNames(this.selections.mappings || []).length
    },
    canStart: function () {
      return (
        this.selections &&
        this.selections.csvRows &&
        this.selections.csvRows.length > 0 &&
        this.mappedColumnCount > 0
      )
    },
  },
  methods: {
    buildRewrittenFile: function () {
      const rows = rewriteCsvRows(
        this.selections.csvRows || [],
        this.selections.mappings || [],
      )
      const csv = Papa.unparse(rows)
      const originalName =
        (this.selections.selectedFile && this.selections.selectedFile.name) ||
        "import.csv"
      return new File([csv], originalName, { type: "text/csv" })
    },
    async importData() {
      this.importError = null
      this.importing = true
      try {
        const path = "/documents/" + this.accountId + "/imports/"
        const file = this.buildRewrittenFile()
        if (this.dataType === "atomicSelected") {
          const success = await this.$store.dispatch("importAtomicData", {
            caseTypeId: this.caseType.id,
            fields: (this.selections.fields || []).map(function (f) {
              return f.name
            }),
            primaries: this.selections.primaries || [],
            shouldCreate: this.noMatchBehaviour === "create",
            shouldUpdate: this.matchFoundBehaviour === "update",
            importData: path,
            file: file,
          })
          if (success) this.$router.back()
        } else {
          const success = await this.$store.dispatch("importTabularData", {
            caseTypeId: this.caseType.id,
            table: this.selections.table,
            tablePrimary: this.selections.tablePrimary,
            primaries: (this.selections.primaries || []).map(function (f) {
              return f.name
            }),
            fields: (this.selections.fields || []).map(function (f) {
              return f.name
            }),
            shouldCreate: this.noMatchBehaviour === "create",
            shouldUpdate: this.matchFoundBehaviour === "update",
            importData: path,
            file: file,
          })
          if (success) this.$router.back()
        }
      } catch (err) {
        this.importError = (err && err.message) || "Import failed"
      } finally {
        this.importing = false
      }
    },
  },
}
</script>
